/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import {
    IpGroupModel,
    LbErrorService,
    NetworkingService,
    NetworkInterfaceModel,
    StaticRouteModel,
    StaticRoutesApiService,
    StaticRoutesService
} from '../../../../shared';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    UtilsService
} from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ipValidator } from '../../../../../../core/directives';

@Component({
    selector: 'app-add-edit-static-route',
    templateUrl: './add-edit-static-route.component.html'
})

export class AddEditStaticRouteComponent extends BaseComponent {
    /**
     * New static FormGroup.
     */
    public staticRouteForm: FormGroup;

    /**
     * Store the id of route being edited.
     */
    public editId: string = null;

    /**
     * Store for groups dropdown content.
     */
    public dropdownGroups: DropdownModel[] = [];

    /**
     * Store for types dropdown content.
     */
    public dropdownTypes: DropdownModel[] = [
        <DropdownModel>{ key: 'default', value: 'Default' },
        <DropdownModel>{ key: 'static', value: 'Static' },
    ];

    /**
     * IP groups array.
     */
    private groups: IpGroupModel[] = [];

    /**
     * Static routes objects.
     */
    private routes: StaticRouteModel[] = [];

    /**
     * Interfaces.
     */
    private interfaces: NetworkInterfaceModel[] = [];

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        public modalService: ModalService,
        public navigationService: NavigationService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private networkingService: NetworkingService,
        private route: ActivatedRoute,
        private router: Router,
        private staticRoutesApiService: StaticRoutesApiService,
        private staticRoutesService: StaticRoutesService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupFormGroups();
        this.setupFormSubscribers();
        this.setUpPageData();
    }

    /**
     * Link to groups page.
     */
    public linkToGroups(): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/network/ip-groups')).then().catch();
    }

    /**
     * Navigate back to the correct page.
     */
    public cancelButton(): void {
        this.navigateToURL('/network/static/routes');
    }

    /**
     * Default gateway check before submit.
     */
    public defaultRouteChangeCheck(): void | Observable<any> {
        const isDefault: boolean = this.initialFormData.type === 'default';
        const hasChanged: boolean = this.initialFormData.gateway !== this.staticRouteForm.get('gateway').value;
        if (hasChanged && isDefault) {
            this.confirmDefaultRouteChange();
        } else {
            return new Observable(observer => {
                this.submitRoute()
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe(
                        () => observer.next(),
                        error => observer.error(error),
                        () => observer.complete()
                    );
            });
        }
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }

    /**
     * Confirmation warning that by changing default route can break networking!
     * @private
     */
    private confirmDefaultRouteChange(): void {
        const confirmationObject = {
            title: 'static_route_default_gw_change',
            message: 'static_route_default_gw_change_message',
            options: { translateText: true },
            callBack: this.submitRoute.bind(this)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Add/Edit route.
     * @private
     */
    private submitRoute(): Observable<any> {
        return new Observable((observer) => {
            const apiCall = this.getApiCall();
            apiCall.pipe(
                takeUntil(this.destroyed$))
                .subscribe(
                    () => {
                        this.navigateToURL('/network/static/routes');
                        observer.next();
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        this.lbErrorService.pageWithErrors(this.staticRouteForm, error.error.errors, 'static-routes-post');
                        const message = error?.message ? error.message : 'Error submitting static route';
                        this.logService.error('submitStaticRoute', message);
                        this.staticRouteForm.get('gateway').markAsPristine();
                        observer.error(error);
                        observer.complete();
                    },
                    () => {
                        this.loadingService.loadingButtonEmitter.next(false);
                    });
        });
    }

    /**
     * Call the relevant API function.
     * @private
     */
    private getApiCall(): any {
        const routeId = this.staticRouteForm.get('id').value;
        const gateway = this.staticRouteForm.get('gateway').value;
        const groupId = this.staticRouteForm.get('groupId').value;
        const type = this.staticRouteForm.get('type').value;

        return routeId ?
            this.staticRoutesApiService.editRoute(routeId, { gateway, type, groupId }) :
            this.staticRoutesApiService.addRoute({ gateway, type, groupId });
    }

    /**
     * Setup static form.
     * @private
     */
    private setupFormGroups(): void {
        this.staticRouteForm = this.formBuilder.group({
            id: [null],
            gateway: [null, [Validators.required, ipValidator]],
            type: ['static', [Validators.required]],
            groupId: [null, [Validators.required]]
        });
        this.initialFormData = this.staticRouteForm.value;
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.staticRouteForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    if (this.editId) {
                        this.utilsService.debounce('form', () => {
                            if (!this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                                this.staticRouteForm.markAsPristine();
                            }
                        });
                    } else {
                        // No editId or initialFormData; Add new form data; mark as dirty (fix button state on error)
                        this.staticRouteForm.markAsDirty();
                    }
                }
            });

        const formGateway = this.staticRouteForm.get('gateway');
        const formGroupId = this.staticRouteForm.get('groupId');

        formGateway.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: gatewayAddress => {
                    this.utilsService.debounce('checkForDuplicateGateway', () => {
                        if (gatewayAddress !== this.initialFormData.gateway) {
                            this.checkForDuplicateGateway(gatewayAddress, formGateway);
                        }
                    }, 500);
                }
            });

        formGroupId.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: groupId => {
                    this.utilsService.debounce('checkForDuplicateGateway', () => {
                        if (groupId !== this.initialFormData.groupId) {
                            this.checkForDuplicateGateway(formGateway.value, formGroupId);
                        }
                    }, 500);
                }
            });
    }

    /**
     * Check for duplicate gateway on group.
     * @param gatewayIp
     * @param formGW
     * @private
     */
    private checkForDuplicateGateway(gatewayIp: string, formGW: AbstractControl): void {
        const groupId = this.staticRouteForm.get('groupId').value;
        const matchGW: StaticRouteModel = this.routes.find(
            item => item.gateway === gatewayIp &&
                item.id !== this.editId &&
                item.groupId === groupId
        );
        if (matchGW) {
            formGW.setErrors({ 'duplicateStaticRoute': `${this.languageService.translate('duplicate_route')}` });
            formGW.markAsTouched();
        }
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.staticRoutesService.getStaticRouteData()
            .pipe()
            .subscribe(
                data => {
                    this.groups = data['groups'];
                    this.routes = data['routes'];
                    this.interfaces = data['interfaces'];
                    if (!this.groups.length) {
                        this.modalService.openModalInParent('noIPGroupsModal');
                    } else {
                        this.dropdownGroups = this.getDropDownGroups();
                        this.setUpPage();
                    }
                }
            );
    }

    /**
     * Get our structured object array for our dropdown.
     * @private
     */
    private getDropDownGroups(): DropdownModel[] {
        const groups: DropdownModel[] = [];
        for (const group of this.groups) {
            const assignedInterface = this.networkingService.getLocalAssignedInterface(group, this.interfaces);
            if (assignedInterface) {
                groups.push(<DropdownModel>{
                    key: group.id,
                    value: `${group.name} (${assignedInterface.settings.interfaceName})`
                });
            }
        }

        return groups;
    }

    /**
     * Set up the page based on id.
     * @private
     */
    private setUpPage(): void {
        this.editId = this.editId || this.route.snapshot.paramMap.get('id');
        if (this.editId) {
            const selectedRoute = this.routes.find(item => item.id === this.editId);
            this.patchForm(selectedRoute);
        }
    }

    /**
     * patch the selected data in the form.
     * @param selectedRoute
     * @private
     */
    private patchForm(selectedRoute: StaticRouteModel): void {
        this.staticRouteForm.patchValue(selectedRoute, { emitEvent: false });
        this.staticRouteForm.controls['groupId'].disable();
        this.initialFormData = this.staticRouteForm.value;
        if (selectedRoute.type === 'default') {
            this.staticRouteForm.controls['type'].disable();
            this.initialFormData.type = selectedRoute.type;
        }
    }
}
